import { useState } from 'react';
import { useMutation } from 'react-query';
import { api } from '../../../services/api';
import { Panel, PanelHeader, PanelBody } from '../../ui/Panel';
import Button from '../../ui/Button';
import { ErrorNotice, InfoNotice, SkeletonRows } from '../../ui/States';
import { errorMessage, selectClass } from '../../../lib/programs';
import { fmt } from '../../../lib/format';
import { shortDay, useInfoRefresh, useSessionFeedback } from '../../../lib/eventInfo';
import type { SimplePanelProps } from '../adapt';

interface PollResult {
  dryRun: boolean;
  session: string;
  responses: number;
  polls: { name: string; responses: number; members: number; created: number; unchanged: number }[];
  warnings: string[];
}

/** Feedback per agenda session (After → Feedback): website answers matched to sessions, and a session's Zoom poll. */
export default function SessionFeedbackPanel({ eventId, canEdit }: SimplePanelProps) {
  const q = useSessionFeedback(eventId);
  const refresh = useInfoRefresh(eventId);
  const [error, setError] = useState<string | null>(null);
  const [pollSession, setPollSession] = useState('');
  const [file, setFile] = useState<{ name: string; text: string } | null>(null);
  const [result, setResult] = useState<PollResult | null>(null);
  const map = useMutation(
    async (body: Record<string, unknown>) => api.put(`/events/${eventId}/feedback/mapping`, body),
    {
      onSuccess: refresh,
      onError: (err) => setError(errorMessage(err, 'Could not save.')),
    },
  );
  const upload = useMutation(
    async (confirm: boolean) =>
      (
        await api.post(`/events/${eventId}/feedback/zoom-poll`, {
          sessionId: pollSession,
          fileName: file?.name,
          content: file?.text,
          confirm,
        })
      ).data.data as PollResult,
    {
      onSuccess: (r) => {
        setResult(r);
        if (!r.dryRun) {
          setFile(null);
          refresh();
        }
      },
      onError: (err) => setError(errorMessage(err, 'Could not read the poll report.')),
    },
  );

  if (q.isLoading) return <SkeletonRows rows={4} />;
  if (q.error || !q.data)
    return <ErrorNotice>{errorMessage(q.error, 'Could not load the session feedback.')}</ErrorNotice>;
  const d = q.data;
  const titleOf = (id: string | null) => d.agenda.find((s) => s.id === id)?.title ?? '';
  const sessionSelect = (value: string | null, onChange: (v: string | null) => void) => (
    <select
      className={selectClass}
      value={value ?? ''}
      disabled={!canEdit || map.isLoading}
      onChange={(e) => onChange(e.target.value || null)}
    >
      <option value="">Not matched</option>
      {d.agenda.map((s) => (
        <option key={s.id} value={s.id}>
          {shortDay(s.day)} {s.startTime} · {s.title}
        </option>
      ))}
    </select>
  );
  const rated = d.sessions.filter((s) => s.responses);
  const guesses = d.answers.filter((a) => !a.sessionId && a.guess);

  return (
    <Panel>
      <PanelHeader
        title="Feedback by session"
        note={`${fmt(d.responses)} response(s) · ${d.withFeedback} session(s) · ${d.unassigned} not matched`}
      />
      <PanelBody className="text-small">
        {rated.length ? (
          <table className="w-full mb-4">
            <thead>
              <tr className="text-left text-small text-ink-2">
                <th className="py-1">Session</th>
                <th>Responses</th>
                <th>Averages</th>
                <th>Poll answers</th>
              </tr>
            </thead>
            <tbody>
              {rated.map((s) => (
                <tr key={s.sessionId} className="border-t border-line align-top">
                  <td className="py-[6px]">
                    {shortDay(s.day)} {s.startTime} · {s.title}
                  </td>
                  <td className="tabular">{fmt(s.responses)}</td>
                  <td>
                    {s.numeric.length
                      ? s.numeric.map((n) => `${n.label}: ${n.mean ?? '—'} (${n.answered})`).join('; ')
                      : '—'}
                  </td>
                  <td>
                    {s.polls.length
                      ? s.polls
                          .map((p) => `${p.question}: ${p.answers.map((a) => `${a.value} ${a.count}`).join(', ')}`)
                          .join(' · ')
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-ink-2 mb-3">No feedback is matched to a session yet.</p>
        )}

        {d.answers.length ? (
          <>
            <div className="text-small text-ink-2 mb-1">Sessions named in the feedback form</div>
            {canEdit && guesses.length ? (
              <Button
                size="sm"
                variant="outline"
                disabled={map.isLoading}
                onClick={() => {
                  setError(null);
                  map.mutate({ answers: Object.fromEntries(guesses.map((g) => [g.value, g.guess])) });
                }}
              >
                Use the {guesses.length} suggestion(s)
              </Button>
            ) : null}
            <ul className="mt-1">
              {d.answers.map((a) => (
                <li key={a.value} className="flex items-center gap-2 border-b border-line py-[5px] flex-wrap">
                  <span className="flex-1">
                    “{a.value}” <span className="text-ink-2">· {a.count}</span>
                    {!a.sessionId && a.guess ? (
                      <span className="text-ink-2"> · suggested: {titleOf(a.guess)}</span>
                    ) : null}
                  </span>
                  {sessionSelect(a.sessionId, (v) => {
                    setError(null);
                    map.mutate({ answers: { [a.value]: v } });
                  })}
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {d.editions.length ? (
          <>
            <div className="text-small text-ink-2 mt-4 mb-1">Feedback forms and polls on this event</div>
            <ul>
              {d.editions.map((e) => (
                <li key={e.id} className="flex items-center gap-2 border-b border-line py-[5px] flex-wrap">
                  <span className="flex-1">
                    {e.label || e.title}{' '}
                    <span className="text-ink-2">
                      · {e.zoomPoll ? 'Zoom poll' : 'website form'} · {e.responses}
                    </span>
                  </span>
                  {sessionSelect(e.sessionId, (v) => {
                    setError(null);
                    map.mutate({ editions: { [e.id]: v } });
                  })}
                </li>
              ))}
            </ul>
            <p className="text-small text-ink-2 mt-1">
              A form or poll tied to one session counts all its answers for that session; leave “Not matched” for an
              all-sessions survey.
            </p>
          </>
        ) : null}

        {canEdit ? (
          <div className="mt-4 border-t border-line pt-3">
            <div className="text-small text-ink-2 mb-1">Upload a session’s Zoom poll report (CSV)</div>
            <div className="flex gap-2 items-center flex-wrap">
              <select className={selectClass} value={pollSession} onChange={(e) => setPollSession(e.target.value)}>
                <option value="">Choose the session…</option>
                {d.agenda.map((s) => (
                  <option key={s.id} value={s.id}>
                    {shortDay(s.day)} {s.startTime} · {s.title}
                    {s.zoomLink ? ' (Zoom)' : ''}
                  </option>
                ))}
              </select>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  setResult(null);
                  setFile(f ? { name: f.name, text: await f.text() } : null);
                }}
              />
              <Button
                size="sm"
                variant="outline"
                disabled={!pollSession || !file || upload.isLoading}
                onClick={() => {
                  setError(null);
                  upload.mutate(false);
                }}
              >
                Check
              </Button>
              {result?.dryRun ? (
                <Button size="sm" disabled={upload.isLoading} onClick={() => upload.mutate(true)}>
                  Import
                </Button>
              ) : null}
            </div>
            {result ? (
              <InfoNotice>
                {result.dryRun ? 'Check: ' : 'Imported: '}
                {result.responses} answer(s) for {result.session}
                {result.polls
                  .map((p) => ` · ${p.name}: ${p.members} member(s), ${p.created} new, ${p.unchanged} already here`)
                  .join('')}
                {result.warnings.length ? ` · ${result.warnings.join(' ')}` : ''}
              </InfoNotice>
            ) : null}
          </div>
        ) : null}
        {error ? <ErrorNotice>{error}</ErrorNotice> : null}
      </PanelBody>
    </Panel>
  );
}
